import React from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import Text, { TextVariant } from "@/components/ui/text";
import ScoreBars, { ScoreStrength } from "@/components/controls/ScoreBars";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { IcpFit, IcpFitSubject } from "@/entities/icpFit/types";
import {
  getIcpFitTKey,
  getIcpFitUnknownTooltipTKey,
  icpFitToScoreBarsMap
} from "@/entities/icpFit/helpers";

const EMPTY_VALUE = "-";

interface Props {
  value: IcpFit | null | undefined;
  isQuickView?: boolean;
  // Table-cell sizing: body_normal/sm (12px) with tighter spacing, per Figma.
  // Detail/KPI usages stay at the larger 18px label size.
  compact?: boolean;
  // Which record the score describes. Only drives the Unknown tooltip copy —
  // the shared table renderer has no entity type to hand in, so it falls back
  // to wording that is true of either subject.
  subject?: IcpFitSubject;
  // The never-scored "-" is a KPI headline on the person/company overview and
  // sits at that page's larger empty-state size; everywhere else it matches the
  // label size. Only the empty state varies, so it is a separate override
  // rather than a second variant scale.
  emptyVariant?: TextVariant;
}

const IcpFitValue: React.FC<Props> = ({
  value,
  isQuickView,
  compact,
  subject,
  emptyVariant
}) => {
  const { t } = useTranslation();

  const textVariant = compact
    ? "smBody"
    : isQuickView
      ? "lgLabel"
      : "smSubtitle";

  // Never scored, or an out-of-range int from the API: the empty state. The
  // out-of-range guard also keeps ScoreBars from receiving an undefined score
  // (which would render a broken bg-* class).
  if (isNil(value) || icpFitToScoreBarsMap[value] === undefined) {
    return <Text variant={emptyVariant ?? textVariant}>{EMPTY_VALUE}</Text>;
  }

  const indicator = (
    <span
      className={`inline-flex items-center min-w-0 max-w-full ${
        compact ? "gap-1.5" : "gap-2"
      }`}
    >
      <ScoreBars score={icpFitToScoreBarsMap[value] as ScoreStrength} />
      {/* min-w-0 on the item itself, not just the container — a flex item's
          min-width: auto otherwise refuses to shrink below its content and
          `truncate` never ellipses in a narrow table column. */}
      <Text as="span" variant={textVariant} className="min-w-0 truncate">
        {t(getIcpFitTKey(value))}
      </Text>
    </span>
  );

  // Scored, but enrichment could not verify the subject. The bars carry no
  // strength, so the only way a user learns why is the hover explanation —
  // which is why the trigger stays a real button: content available on hover
  // has to be reachable on focus too.
  if (value === IcpFit.UNKNOWN) {
    return (
      <Tooltip>
        <TooltipTrigger className="min-w-0 max-w-full text-left">
          {indicator}
        </TooltipTrigger>
        <TooltipContent>
          {t(getIcpFitUnknownTooltipTKey(subject))}
        </TooltipContent>
      </Tooltip>
    );
  }

  return indicator;
};

export default IcpFitValue;
